import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface InstanceAssignment {
  id: string;
  instance_id: string;
  instance?: {
    id: string;
    identifier: string;
  } | null;
}

interface AvailableInstance {
  id: string;
  identifier: string;
  status: string;
}

interface InstanceTagsInputProps {
  assignedInstances: InstanceAssignment[];
  availableInstances: AvailableInstance[];
  onAssign: (instanceId: string) => void;
  onUnassign: (assignmentId: string) => void;
  compact?: boolean;
  maxInstances?: number;
}

export function InstanceTagsInput({
  assignedInstances,
  availableInstances,
  onAssign,
  onUnassign,
  compact = false,
  maxInstances,
}: InstanceTagsInputProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const atLimit =
    maxInstances !== undefined && assignedInstances.length >= maxInstances;

  const filteredInstances = availableInstances.filter((inst) =>
    inst.identifier.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const handleSelect = (instanceId: string) => {
    if (atLimit) return;
    onAssign(instanceId);
    setInputValue("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === "Backspace" &&
      inputValue === "" &&
      assignedInstances.length > 0
    ) {
      const lastAssignment = assignedInstances[assignedInstances.length - 1];
      onUnassign(lastAssignment.id);
    }
  };

  if (compact) {
    return (
      <Popover open={!atLimit && open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className="flex flex-wrap items-center gap-1 min-h-[28px] px-2 py-1 cursor-pointer hover:bg-muted rounded"
            onClick={() => !atLimit && setOpen(true)}
          >
            {assignedInstances.length > 0 ? (
              assignedInstances.map((ia) => (
                <Badge
                  key={ia.id}
                  variant="secondary"
                  className="text-xs h-5 px-1.5 gap-0.5"
                >
                  {ia.instance?.identifier || ia.instance_id.slice(0, 8)}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnassign(ia.id);
                    }}
                    className="ml-0.5 hover:text-destructive"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command>
            <CommandInput
              ref={inputRef}
              placeholder="Type to search..."
              value={inputValue}
              onValueChange={setInputValue}
              onKeyDown={handleKeyDown}
            />
            <CommandList>
              <CommandEmpty>No instances available</CommandEmpty>
              <CommandGroup>
                {filteredInstances.map((inst) => (
                  <CommandItem
                    key={inst.id}
                    value={inst.identifier}
                    onSelect={() => handleSelect(inst.id)}
                  >
                    {inst.identifier}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="space-y-2">
      <div
        className="flex flex-wrap gap-2 min-h-[40px] p-2 border rounded-md bg-background cursor-text"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {assignedInstances.map((ia) => (
          <Badge
            key={ia.id}
            variant="secondary"
            className="flex items-center gap-1"
          >
            {ia.instance?.identifier || ia.instance_id.slice(0, 8)}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onUnassign(ia.id);
              }}
              className="ml-1 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <input
              ref={inputRef}
              type="text"
              className="flex-1 min-w-[120px] bg-transparent outline-none text-sm placeholder:text-muted-foreground"
              disabled={atLimit}
              placeholder={
                atLimit
                  ? ""
                  : assignedInstances.length === 0
                    ? "Type to add instances..."
                    : "Add more..."
              }
              value={inputValue}
              onChange={(e) => {
                if (atLimit) return;
                setInputValue(e.target.value);
                if (!open) setOpen(true);
              }}
              onFocus={() => !atLimit && setOpen(true)}
              onKeyDown={handleKeyDown}
            />
          </PopoverTrigger>
          <PopoverContent
            className="w-64 p-0"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command>
              <CommandList>
                <CommandEmpty>No instances available</CommandEmpty>
                <CommandGroup>
                  {filteredInstances.map((inst) => (
                    <CommandItem
                      key={inst.id}
                      value={inst.identifier}
                      onSelect={() => handleSelect(inst.id)}
                    >
                      {inst.identifier}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
